import { Controller } from "@hotwired/stimulus"

// Builds a table of contents from headings in a content area.
// Usage:
//   <div data-controller="table-of-contents" data-table-of-contents-content-selector-value=".article-content">
//     <nav data-table-of-contents-target="nav"></nav>
//   </div>

export default class extends Controller {
  static targets = ["nav"]
  static values = { contentSelector: { type: String, default: ".article-content" } }

  connect() {
    // Delay slightly to let linkable-headings add IDs first
    requestAnimationFrame(() => this.buildToc())
  }

  buildToc() {
    const content = document.querySelector(this.contentSelectorValue)
    if (!content || !this.hasNavTarget) return

    const headings = content.querySelectorAll("h1, h2, h3")
    if (headings.length === 0) {
      this.navTarget.innerHTML = '<p class="text-xs text-gray-400 italic">No headings found</p>'
      return
    }

    const list = document.createElement("ul")
    list.className = "space-y-1"

    headings.forEach((heading) => {
      if (!heading.id) return

      const li = document.createElement("li")
      const link = document.createElement("a")

      // Clean text (remove the # anchor link text appended by linkable-headings)
      const text = heading.textContent.replace(/#$/, "").trim()
      link.textContent = text
      link.href = `#${heading.id}`
      link.dataset.headingId = heading.id
      link.dataset.action = "click->table-of-contents#scrollTo"

      // Indent based on heading level
      const level = parseInt(heading.tagName.charAt(1))
      const indent = level === 1 ? "" : level === 2 ? "ml-3" : "ml-6"
      const weight = level === 1 ? "font-semibold" : "font-normal"
      link.className = `block py-1 text-xs ${weight} ${indent} text-gray-600 hover:text-sapphire truncate transition-colors`

      li.appendChild(link)
      list.appendChild(li)
    })

    this.navTarget.innerHTML = ""
    this.navTarget.appendChild(list)

    // Set up scroll tracking
    this.observeHeadings(headings)
  }

  scrollTo(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.headingId
    const target = document.getElementById(id)
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  observeHeadings(headings) {
    // Find the scrollable container for the article content
    const content = document.querySelector(this.contentSelectorValue)
    const scrollRoot = content?.closest(".overflow-y-scroll") || null

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.setActive(entry.target.id)
        }
      })
    }, {
      root: scrollRoot,
      rootMargin: "-10% 0px -80% 0px",
      threshold: 0
    })

    headings.forEach((heading) => {
      if (heading.id) this.observer.observe(heading)
    })
  }

  setActive(id) {
    if (!this.hasNavTarget) return

    this.navTarget.querySelectorAll("a").forEach((link) => {
      if (link.dataset.headingId === id) {
        link.classList.remove("text-gray-600")
        link.classList.add("text-sapphire", "font-semibold")
      } else {
        link.classList.remove("text-sapphire", "font-semibold")
        link.classList.add("text-gray-600")
        // Restore weight for h1 links
        if (!link.classList.contains("ml-3") && !link.classList.contains("ml-6")) {
          link.classList.add("font-semibold")
        } else {
          link.classList.remove("font-semibold")
          link.classList.add("font-normal")
        }
      }
    })
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }
}
